import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import api from '../services/api'

// Emergency-lock page (adversarial-review finding #23).
//
// Reached from the "wasn't me" link in the confirmation email that
// follows a successful password reset.  Redeeming the token deactivates
// the account until an administrator investigates.
//
// Token is delivered in the URL fragment (`#t=<token>`) so it never
// hits Apache logs / Referer / browser history — mirror of the
// engagement-sign + Stripe-pay pattern.

function readTokenFromHashOrStorage() {
  try {
    const raw = window.location.hash || ''
    const match = raw.match(/(?:^|[#&])t=([^&]+)/)
    if (match && match[1]) {
      const t = decodeURIComponent(match[1])
      try { sessionStorage.setItem('lextrack_emergency_lock_token', t) } catch { /* private mode */ }
      try { window.history.replaceState(null, '', window.location.pathname) } catch { /* noop */ }
      return t
    }
    return sessionStorage.getItem('lextrack_emergency_lock_token') || ''
  } catch {
    return ''
  }
}

export default function EmergencyLock() {
  const token = useMemo(readTokenFromHashOrStorage, [])
  const [phase, setPhase] = useState(token ? 'confirm' : 'no-token') // confirm | working | done | error
  const [error, setError] = useState('')

  useEffect(() => {
    if (!token) setPhase('no-token')
  }, [token])

  const lockNow = async () => {
    setError('')
    setPhase('working')
    try {
      await api.post('/api/auth/emergency-lock', { token })
      try { sessionStorage.removeItem('lextrack_emergency_lock_token') } catch { /* noop */ }
      setPhase('done')
    } catch (err) {
      const status = err.response?.status
      const retryHeader = err.response?.headers?.['retry-after']
      if (status === 429) {
        const seconds = parseInt(retryHeader, 10) || 900
        setError(`Rate limit — please wait ${Math.ceil(seconds / 60)} minute${seconds >= 120 ? 's' : ''} and try again from this network. If this is urgent, contact your firm administrator now.`)
      } else {
        setError(err.response?.data?.error || 'Could not lock the account. The link may have expired or already been used. Contact your firm administrator immediately.')
      }
      setPhase('confirm')
    }
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-lg p-10 w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-primary-600">Omnia Practice Management</h1>
          <p className="text-gray-500 mt-2">Emergency Account Lock</p>
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 rounded-lg p-3 mb-6 text-sm" role="alert">
            {error}
          </div>
        )}

        {phase === 'no-token' && (
          <div className="text-center">
            <div className="bg-amber-50 text-amber-700 rounded-lg p-4 mb-6 text-sm">
              Missing emergency-lock token. This link should have been included in a
              confirmation email after a recent password reset.
            </div>
            <p className="text-xs text-gray-500">
              If you did not request or authorize a password reset, contact your firm
              administrator immediately.
            </p>
          </div>
        )}

        {phase === 'confirm' && (
          <div>
            <div className="bg-red-50 text-red-800 rounded-lg p-4 mb-6 text-sm">
              <div className="font-semibold mb-2">This will immediately deactivate your account.</div>
              <p>
                Use this only if you did NOT request the recent password reset. Your
                account will be locked and an administrator must review and reactivate
                it before you can sign in again.
              </p>
            </div>
            <button
              onClick={lockNow}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold
                         rounded-lg py-2.5 transition-colors"
            >
              🚨 Lock My Account Now
            </button>
            <div className="text-center text-sm mt-4">
              <Link to="/login" className="text-gray-500 hover:underline">
                ← Back to sign in
              </Link>
            </div>
          </div>
        )}

        {phase === 'working' && (
          <div className="text-center text-sm text-gray-600">
            Locking your account…
          </div>
        )}

        {phase === 'done' && (
          <div className="text-center">
            <div className="bg-green-50 text-green-800 rounded-lg p-4 mb-6 text-sm">
              <div className="font-semibold mb-1">Account locked.</div>
              <p>
                All active sessions have been terminated and your firm's administrator
                has been notified. They will investigate and reactivate your account
                as appropriate.
              </p>
            </div>
            <p className="text-xs text-gray-500">
              Please contact your administrator directly to expedite the review.
            </p>
          </div>
        )}

        <p className="text-center text-xs text-gray-400 mt-6">
          Omnia Practice Management © {new Date().getFullYear()}
        </p>
      </div>
    </div>
  )
}
